import React, {useMemo} from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    Button,
    Text,
    Accordion,
    AccordionItem,
    AccordionPanel,
    AccordionButton,
    useMultiStyleConfig,
    Link as ChakraLink
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {defineMessages, useIntl} from 'react-intl'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'

/* Components */
import {Link as RouterLink} from 'react-router-dom'
import {HideOnDesktop, HideOnMobile} from '@salesforce/retail-react-app/app/components/responsive'

/* Icons */
import {ChevronDownType2} from '../../icons/index'

/* Utils */
import {
    resolveGDIStyles,
    getGDIHideStyles,
    hasGDIViewTypes,
    parseGDIViewTypes,
    GDI_TEXT_BLOCK_FIELD_MAP
} from '../../optimizely/gdi/utils/gdi-styles'
import {resolveGDILink, GDI_LINK_TYPES} from '../../optimizely/gdi/utils/gdi-link-resolver'
import {localizeHref} from '../../../utils/url'
import {
    STYLE_VARIANT_MAP,
    extraStylePropsFor,
    isTextLinkStyle,
    getMegaMenuLinkSx
} from '../../optimizely/gdi/utils/gdi-cta-style'
import {sendClickEvent} from '../../../utils/gtm-utils'

import {useGetLinkData} from '../../../contexts/category-url-context'

const CTA_STYLE_MAP = {
    Color: 'color',
    BackgroundColor: 'backgroundColor',
    BorderColor: 'borderColor',
    MinWidth: 'minWidth',
    GDICtaBlockPadding: 'padding',
    Margin: 'margin'
}

const isDesktopHoverEntry = (entry) => parseGDIViewTypes(entry?.ViewTypesToApply).includes('_hover')

const getPrimaryCtaStyleEntry = (stylesArr) => {
    if (!Array.isArray(stylesArr)) return {}
    return (
        stylesArr.find((entry) => !hasGDIViewTypes(entry) && entry?.Style) ||
        stylesArr.find((entry) => !isDesktopHoverEntry(entry) && entry?.Style) ||
        stylesArr.find((entry) => entry?.Style) ||
        {}
    )
}

/* Anchors are inline by default — vertical padding/margin authored in CMS would
 * not visually push siblings. Force inline-block so authored spacing renders. */
const LINK_BOX_DEFAULTS = {display: 'inline-block'}

/* When rendering footer CTAs as Chakra Buttons (variant branch), Chakra's Button
 * already manages display/sizing. We only need to ensure the button doesn't
 * stretch to fill its container and that the variant's own typography/decoration
 * isn't overridden by inherited anchor styling. `textDecoration: 'none'` is a
 * base-state override only — `_hover` is intentionally omitted here so the
 * variant's hover (e.g. webPrimarySmall background change) stays intact. */
const BUTTON_LAYOUT_DEFAULTS = {
    width: 'auto',
    alignSelf: 'flex-start',
    textDecoration: 'none'
}

import {openClaraChat} from '../../../hooks/use-clara-chat'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {getSiteEnvVarValue} from '../../../utils/site-config'
import useHideLoyaltyUIForInternational from '@app/hooks/use-hide-loyalty-ui-for-international'
import {
    isLoyaltyFooterContent,
    parseLoyaltyFooterContentIds
} from '@app/utils/loyalty-footer-content'
/* Global Constants */
const messages = defineMessages({
    customerService: {
        id: 'footer.customerService',
        defaultMessage: 'Customer Service'
    },
    orderTracking: {
        id: 'footer.orderTracking',
        defaultMessage: 'Order Tracking'
    },
    chatWithUs: {
        id: 'footer.chatWithUs',
        defaultMessage: 'Chat With Us'
    }
})

const AccordionIcon = ({isExpanded}) => {
    const styles = useMultiStyleConfig('FooterNavigationLinks', {isExpanded})

    return <ChevronDownType2 {...styles.accordionIcon} />
}

const slugifyFooterLabel = (value) =>
    String(value || '')
        .toLowerCase()
        .trim()
        .replace(/&/g, 'and')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')

// Canonical footer_link click suffixes
const FOOTER_LINK_HREF_RULES = [
    {type: 'email-us', test: (href) => href.includes('email-us') || href.includes('contact-us') || href.startsWith('mailto:')},
    {type: 'find-a-store', test: (href) => /\/stores?(?:\/|$)/.test(href) || href.includes('store-locator')},
    {type: 'careers', test: (href) => href.includes('career')},
    {type: 'affiliate-program', test: (href) => href.includes('affiliate')},
    {type: 'blog', test: (href) => href.includes('/blog')},
    {type: 'order-tracking', test: (href) => href.includes('order-tracking') || href.includes('/orders')},
    {type: 'faq', test: (href) => href.includes('/faq')},
    {
        type: 'shipping-and-delivery',
        test: (href) => href.includes('shipping') || href.includes('delivery')
    },
    {type: 'return-policy', test: (href) => href.includes('return')},
    {type: 'payment-info', test: (href) => href.includes('payment')},
    {type: 'klarna', test: (href) => href.includes('klarna')},
    {
        type: 'gift-card-check-balance',
        test: (href) =>
            href.includes('gift-card') &&
            (href.includes('balance') || href.includes('check') || href.includes('lookup'))
    },
    {
        type: 'gift-card-purchase',
        test: (href) =>
            href.includes('gift-card') || href.includes('giftcard') || href.includes('gift_card')
    },
    {
        type: 'loyalty-program-learn-more',
        test: (href) =>
            (href.includes('my-rewards') || href.includes('loyalty') || href.includes('collectif')) &&
            (href.includes('learn') || href.includes('about'))
    },
    {
        type: 'loyalty-program-join-now',
        test: (href) =>
            href.includes('my-rewards') || href.includes('loyalty') || href.includes('collectif')
    },
    {type: 'about-dynamite', test: (href) => href.includes('about') && href.includes('dynamite')},
    {type: 'about-garage', test: (href) => href.includes('about') && href.includes('garage')},
    {type: 'cookie-preferences', test: (href) => href.includes('cookie')}
]

const FOOTER_LINK_LABEL_RULES = [
    {type: 'chat-with-us', test: (label) => label.includes('chat')},
    {
        type: 'email-us',
        test: (label) =>
            label.includes('email') || label.includes('contact us') || label.includes('contactez')
    },
    {
        type: 'find-a-store',
        test: (label) =>
            (label.includes('store') && (label.includes('locator') || label.includes('find'))) ||
            label.includes('trouver un magasin') ||
            label.includes('magasins')
    },
    {type: 'careers', test: (label) => label.includes('career') || label.includes('carrière')},
    {
        type: 'cookie-preferences',
        test: (label) => label.includes('cookie') && label.includes('preference')
    },
    {
        type: 'affiliate-program',
        test: (label) => label.includes('affiliate') || label.includes('affiliation')
    },
    {type: 'blog', test: (label) => label === 'blog' || label.includes(' blog')},
    {
        type: 'order-tracking',
        test: (label) =>
            (label.includes('order') && label.includes('track')) ||
            label.includes('suivi de commande')
    },
    {type: 'faq', test: (label) => label.includes('faq') || label.includes('frequently asked')},
    {
        type: 'shipping-and-delivery',
        test: (label) =>
            label.includes('shipping') ||
            label.includes('delivery') ||
            label.includes('livraison')
    },
    {
        type: 'return-policy',
        test: (label) => label.includes('return') || label.includes('retour')
    },
    {
        type: 'payment-info',
        test: (label) => label.includes('payment') || label.includes('paiement')
    },
    {type: 'klarna', test: (label) => label.includes('klarna')},
    {
        type: 'gift-card-check-balance',
        test: (label) =>
            (label.includes('gift') && label.includes('balance')) ||
            (label.includes('carte cadeau') && label.includes('solde'))
    },
    {
        type: 'gift-card-purchase',
        test: (label) =>
            (label.includes('gift') &&
                (label.includes('card') || label.includes('purchase') || label.includes('buy'))) ||
            label.includes('carte cadeau')
    },
    {
        type: 'loyalty-program-join-now',
        test: (label) =>
            label.includes('join') ||
            (label.includes('loyalty') && !label.includes('learn')) ||
            label.includes('collectif')
    },
    {
        type: 'loyalty-program-learn-more',
        test: (label) => label.includes('learn more') || label.includes('en savoir plus')
    },
    {
        type: 'about-dynamite',
        test: (label) => label.includes('about') && label.includes('dynamite')
    },
    {
        type: 'about-garage',
        test: (label) => label.includes('about') && label.includes('garage')
    }
]

/**
 * Resolves the TSD custom_click type for a footer link.
 * Prefers href, then label aliases, then slugified label.
 */
const resolveFooterLinkClickType = ({
    label = '',
    href = '',
    isChatWithUs = false
} = {}) => {
    if (isChatWithUs) return 'footer_link_chat-with-us'

    const normalizedLabel = String(label).toLowerCase().trim()
    const normalizedHref = String(href).toLowerCase().trim()

    if (normalizedHref && normalizedHref !== '#') {
        const hrefMatch = FOOTER_LINK_HREF_RULES.find((rule) => rule.test(normalizedHref))
        if (hrefMatch) return `footer_link_${hrefMatch.type}`
    }

    const labelMatch = FOOTER_LINK_LABEL_RULES.find((rule) => rule.test(normalizedLabel))
    if (labelMatch) return `footer_link_${labelMatch.type}`

    const slug = slugifyFooterLabel(normalizedLabel)
    return slug ? `footer_link_${slug}` : 'footer_link_unknown'
}

// GTM Modification
const sendGTMEvent = (link) => {
    sendClickEvent(resolveFooterLinkClickType(link))
}

AccordionIcon.propTypes = {
    isExpanded: PropTypes.bool
}

const NavigationLinks = ({footerData, ...otherProps}) => {
    const styles = useMultiStyleConfig('FooterNavigationLinks')
    const intl = useIntl()
    const getLinkData = useGetLinkData()
    const config = getConfig()
    const {site, buildUrl} = useMultiSite()
    const siteId = site?.id
    const shouldHideLoyaltyUI = useHideLoyaltyUIForInternational()
    const loyaltyContentIds = parseLoyaltyFooterContentIds(
        config.app?.HIDE_LOYALTY_FOOTER_CONTENT_IDS
    )

    /* Map CMS data to footer links structure */
    const NAV_LINKS_DATA = useMemo(() => {
        if (!footerData?.listOfLinks?.length) {
            // Fallback to show order tracking link when CMS data is not available
            return [
                {
                    sectionId: 'customerService',
                    title: intl.formatMessage(messages.customerService),
                    links: [
                        {
                            label: intl.formatMessage(messages.orderTracking),
                            href: '/order-tracking',
                            id: messages.orderTracking.id
                        }
                    ]
                }
            ]
        }

        // Map CMS data to the expected structure
        return footerData.listOfLinks.map((column, index) => {
            const titleStyles = resolveGDIStyles(column.columnTitleStyle, GDI_TEXT_BLOCK_FIELD_MAP).styles
            const itemStyles = resolveGDIStyles(column.columnLinksStyle, GDI_TEXT_BLOCK_FIELD_MAP).styles
            return {
                sectionId: `section-${index}`,
                title: column.columnTitle || '',
                titleStyles,
                itemStyles,
                links: (column.columnLinks || [])
                    .map((ctaBlock) => {
                        // Respect HideBlockOn — same logic as gdiCtaBlock
                        const hideStyles = getGDIHideStyles(ctaBlock.HideBlockOn)
                        if (hideStyles.doNotLoadComponent) return null

                        const ctaStylesList = ctaBlock.GDICtaBlockStylesPerViewType
                        const style = getPrimaryCtaStyleEntry(ctaStylesList)
                        const label = ctaStylesList?.find?.((entry) => entry?.CtaText)?.CtaText || ''

                        const expandedLink = ctaBlock.GDICtaBlockLink?.[0]?.ContentLink?.Expanded

                        // Map known CMS link references to correct routes
                        const remappedLink =
                            expandedLink?.LinkType === GDI_LINK_TYPES.INTERNAL &&
                            /^\/?orders$/.test(expandedLink.LinkReference || '')
                                ? {...expandedLink, LinkReference: '/order-tracking'}
                                : expandedLink

                        const linkData = resolveGDILink(remappedLink, getLinkData)

                        if (
                            shouldHideLoyaltyUI &&
                            isLoyaltyFooterContent(ctaBlock, loyaltyContentIds, {
                                label,
                                href: linkData?.href
                            })
                        ) {
                            return null
                        }

                        /* Route the authored Style through the same map GDICtaBlock uses.
                         * Tertiary/text styles (e.g. "Underlined on Hover") keep the
                         * existing plain-link rendering; everything else renders as a
                         * Chakra Button with the matching design-system variant so the
                         * footer matches the brand's button treatment. */
                        const styleName = style.Style
                        const variant = isTextLinkStyle(styleName)
                            ? null
                            : STYLE_VARIANT_MAP[styleName] || null
                        const extraStyleProps = variant ? extraStylePropsFor(styleName) : null
                        const styleSx =
                            variant && styleName === 'Mega Menu Link' ? getMegaMenuLinkSx() : null

                        // Check if this is a Store Locator link and if it should be shown
                        const isStoreLocator =
                            label.toLowerCase().includes('store') &&
                            label.toLowerCase().includes('locator')

                        if (isStoreLocator && getSiteEnvVarValue(config.app, 'STORELOCATOR_ENABLED', siteId) !== 'true') {
                            return null
                        }

                        // Check if this is a Cookie Preferences link
                        const isCookiePreferences =
                            label.toLowerCase().includes('cookie') &&
                            label.toLowerCase().includes('preference')

                        // Language-agnostic: a CTA block with no GDICtaBlockLink is a
                        // special-action link (Clara chat trigger). Avoids label matching
                        // which breaks for non-English locales.
                        // Secondary check: label contains 'chat' as a fallback for edge cases.
                        const isChatWithUs =
                            !ctaBlock.GDICtaBlockLink?.length ||
                            label.toLowerCase().includes('chat')
                        return {
                            label,
                            href: linkData?.href || (isCookiePreferences || isChatWithUs ? '#' : null),
                            isExternalUrl: !!linkData?.isExternal,
                            ...(linkData?.target && {target: linkData.target}),
                            ...(linkData?.rel && {rel: linkData.rel}),
                            id: label, // Use label as ID for GTM tracking
                            isChatWithUs,
                            sx: hideStyles.sx,
                            preventDefault: isCookiePreferences,
                            variant,
                            extraStyleProps,
                            styleSx,
                            ctaStyles: resolveGDIStyles(
                                ctaBlock.GDICtaBlockStylesPerViewType,
                                CTA_STYLE_MAP
                            ).styles,
                            onClick: isCookiePreferences
                                ? function () {
                                      if (typeof window !== 'undefined' && window.Didomi) {
                                          window.Didomi.preferences.show()
                                      }
                                  }
                                : null
                        }
                    })
                    .filter((link) => link && link.href)
            }
        })
    },
    [
        footerData,
        intl,
        styles.sectionTitle,
        styles.footerLink,
        getLinkData,
        shouldHideLoyaltyUI,
        loyaltyContentIds,
        siteId
    ])

    /* Single rendering path for footer links. When the CMS-authored Style maps
     * to a design-system button variant (e.g. "Button Solid" → webPrimarySmall),
     * render as a Chakra Button polymorphically (`as={Link/ChakraLink}`) so the
     * brand's button treatment is applied while navigation still flows through
     * react-router for internal hrefs. Tertiary/text styles keep the existing
     * inline-link rendering. */
    const renderLink = (link, section) => {
        const dataTestId = `footer-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`
        const handleClick = (e) => {
            if (link.isChatWithUs) {
                openClaraChat(e)
                if (link.id) {
                    sendGTMEvent(link)
                }
                return
            }
            if (link.preventDefault) {
                e.preventDefault()
                if (link.onClick) {
                    link.onClick()
                }
            }
            if (link.id) {
                sendGTMEvent(link)
            }
        }
        const sx = {...LINK_BOX_DEFAULTS, ...section.itemStyles, ...link.ctaStyles}
        const to =
            link.isExternalUrl || link.href === '#'
                ? link.href
                : localizeHref(link.href, buildUrl)

        if (link.variant) {
            /* Button branch: skip LINK_BOX_DEFAULTS (its inline-block conflicts with
             * Chakra Button's inline-flex layout) and skip styles.footerLink (theme
             * footer-link text styling does not apply to a real button). Only the
             * style-specific pseudo sx (e.g. Mega Menu underline animation) and the
             * CMS-authored ctaStyles are layered on top of the variant. */
            const buttonSx = {...(link.styleSx || {}), ...section.itemStyles, ...link.ctaStyles}
            const buttonProps = {
                'data-testid': dataTestId,
                variant: link.variant,
                ...BUTTON_LAYOUT_DEFAULTS,
                ...(link.extraStyleProps || {}),
                sx: buttonSx,
                onClick: handleClick
            }
            return link.isExternalUrl ? (
                <Button
                    as={ChakraLink}
                    href={link.href}
                    isExternal={link.target === '_blank'}
                    {...(link.target && {target: link.target})}
                    {...(link.rel && {rel: link.rel})}
                    {...buttonProps}
                >
                    {link.label}
                </Button>
            ) : (
                <Button as={RouterLink} to={to} {...buttonProps}>
                    {link.label}
                </Button>
            )
        }

        return link.isExternalUrl ? (
            <ChakraLink
                data-testid={dataTestId}
                href={link.href}
                isExternal={link.target === '_blank'}
                {...(link.target && {target: link.target})}
                {...(link.rel && {rel: link.rel})}
                {...styles.footerLink}
                sx={sx}
                onClick={handleClick}
            >
                {link.label}
            </ChakraLink>
        ) : (
            <ChakraLink
                as={RouterLink}
                data-testid={dataTestId}
                to={to}
                {...styles.footerLink}
                sx={sx}
                onClick={handleClick}
            >
                {link.label}
            </ChakraLink>
        )
    }

    return (
        <React.Fragment {...otherProps}>
            <HideOnDesktop as={Accordion} allowMultiple>
                {NAV_LINKS_DATA.map((section) => (
                    <AccordionItem key={section.sectionId} {...styles.accordionItem}>
                        {({isExpanded}) => (
                            <>
                                <AccordionButton
                                    data-testid={`footer-accordion-button-${section.sectionId}`}
                                    {...styles.accordionButton}
                                >
                                    <Text {...styles.sectionTitle} sx={section.titleStyles || {}}>
                                        {section.title}
                                    </Text>
                                    <AccordionIcon isExpanded={isExpanded} />
                                </AccordionButton>
                                <AccordionPanel
                                    {...(isExpanded
                                        ? styles.accordionPanelExpanded
                                        : styles.accordionPanel)}
                                >
                                    {section.links.map((link) => (
                                        <Box key={link.label} {...styles.linkContainer} {...(link.sx && {sx: link.sx})}>
                                            {renderLink(link, section)}
                                        </Box>
                                    ))}
                                </AccordionPanel>
                            </>
                        )}
                    </AccordionItem>
                ))}
            </HideOnDesktop>
            {NAV_LINKS_DATA.map((section) => (
                <HideOnMobile key={section.sectionId}>
                    <Box>
                        <Text {...styles.sectionTitle} sx={section.titleStyles || {}}>
                            {section.title}
                        </Text>
                        {section.links.map((link) => (
                            <Box key={link.label} {...styles.linkContainer} {...(link.sx && {sx: link.sx})}>
                                {renderLink(link, section)}
                            </Box>
                        ))}
                    </Box>
                </HideOnMobile>
            ))}
        </React.Fragment>
    )
}

NavigationLinks.propTypes = {
    footerData: PropTypes.object,
    otherProps: PropTypes.object
}

export default NavigationLinks
